// R1b-β Step 2 Deliverable B — User detail page.
// Reachable via /admin/users/:id from UsersTab email links.
// Actions live server-side; frontend never fakes permission checks.
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import UserProjectAssignments from "../../components/admin/UserProjectAssignments";
import { Send, ShieldOff, ShieldCheck, ClipboardList, Info } from "lucide-react";

export default function UserDetail() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [roles, setRoles] = useState([]);
  const [history, setHistory] = useState({ rows: [], count: 0 });
  const [busy, setBusy] = useState(false);
  const [inviteNote, setInviteNote] = useState("");
  const [form, setForm] = useState({ display_name: "", role_key: "" });

  const load = async () => {
    const [u, r, h] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/roles`),
      api.get(`/users/${id}/access-history?limit=50`).catch(() => ({ data: { rows: [], count: 0 } })),
    ]);
    setTarget(u.data);
    setRoles(r.data);
    setHistory(h.data);
    setForm({ display_name: u.data.display_name || "", role_key: u.data.role_key });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canManage = me?.role_key === "OWNER_SUPER_ADMIN" || me?.role_key === "ADMINISTRATOR";
  const isSelf = me?.id === id;
  const isOwnerTarget = target?.role_key === "OWNER_SUPER_ADMIN";
  const canDemoteOwner = me?.role_key === "OWNER_SUPER_ADMIN";

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${id}`, { display_name: form.display_name });
      toast.success("Profile updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveRole = async () => {
    if (isSelf && form.role_key !== target.role_key) {
      toast.error("You cannot change your own role.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/users/${id}`, { role_key: form.role_key });
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/users/${id}/resend-invite`);
      setInviteNote(r.data.delivery_label);
      toast.success(`Invite resent · ${r.data.delivery_label}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async () => {
    if (isSelf) { toast.error("You cannot change your own status."); return; }
    const action = target.disabled ? "restore" : "suspend";
    if (!window.confirm(`${action === "suspend" ? "Suspend" : "Restore"} this user?`)) return;
    setBusy(true);
    try {
      await api.patch(`/users/${id}/status`, { action });
      toast.success(`User ${action}d`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!target) {
    return (
      <AdminAppShell activeNav="people-access" title="User" subtitle="Loading…" user={me}>
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6 lg:px-8" data-testid="user-detail-loading">
          <div className="font-mono text-xs text-erf-dark/60">Loading user…</div>
        </div>
      </AdminAppShell>
    );
  }

  const statusLabel = target.disabled ? "Suspended"
    : target.invited ? "Invited"
    : target.must_rotate_password ? "Awaiting rotation"
    : "Active";
  const statusStatus = target.disabled ? "error"
    : target.invited ? "pending"
    : target.must_rotate_password ? "pending"
    : "connected";

  return (
    <AdminAppShell
      activeNav="people-access"
      title={target.display_name || target.email}
      subtitle={target.email}
      breadcrumbs={[
        { label: "People & Access", to: "/admin/people-roles/users" },
        { label: "Users", to: "/admin/people-roles/users" },
        { label: target.display_name || target.email, to: null },
      ]}
      user={me}
    >
      <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6 lg:px-8 grid gap-4 md:grid-cols-2" data-testid="user-detail-page">
        <Card className="md:col-span-2 border-erf-borderCream bg-erf-cream" data-testid="user-detail-header">
          <CardContent className="p-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">User · {target.role_key}</div>
              <div className="font-display font-semibold text-erf-dark text-xl mt-1">{target.display_name || "—"}</div>
              <div className="text-xs font-mono text-erf-dark/70 mt-0.5">{target.email}</div>
              <div className="mt-2 flex items-center gap-2">
                <StatusChip status={statusStatus} label={statusLabel} />
                <span className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">
                  MFA: not enrolled · phase 5
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {target.invited && canManage && (
                <Button variant="outline" onClick={resend} disabled={busy}
                        data-testid="user-detail-resend-invite" className="border-erf-borderCream">
                  <Send className="w-4 h-4 mr-1" /> Resend invite
                </Button>
              )}
              {canManage && !isSelf && !(isOwnerTarget && !canDemoteOwner) && (
                <Button variant="outline" onClick={toggleStatus} disabled={busy}
                        data-testid={target.disabled ? "user-detail-restore" : "user-detail-suspend"}
                        className="border-erf-borderCream">
                  {target.disabled ? <><ShieldCheck className="w-4 h-4 mr-1"/> Restore</> : <><ShieldOff className="w-4 h-4 mr-1"/> Suspend</>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {inviteNote && (
          <Card className="md:col-span-2 border-erf-gold bg-erf-creamSecondary" data-testid="user-detail-invite-note">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-4 h-4 text-erf-gold mt-0.5"/>
              <div className="text-sm font-nav text-erf-dark">
                {inviteNote}. The previous link no longer works. For security, the new link is sent only by email and
                is never shown here; it expires in 7 days.
              </div>
            </CardContent>
          </Card>
        )}

        <UserProjectAssignments userId={id} targetRole={target.role_key} />

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="user-detail-profile">
          <CardContent className="p-5 space-y-3">
            <div className="font-display font-semibold text-erf-dark">Profile</div>
            <div>
              <Label className="text-xs uppercase font-nav">Display name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     className="mt-1" data-testid="user-detail-display-name" disabled={!canManage && !isSelf}/>
            </div>
            <Button onClick={saveProfile} disabled={busy || (!canManage && !isSelf)}
                    className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                    data-testid="user-detail-save-profile">
              Save profile
            </Button>
          </CardContent>
        </Card>

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="user-detail-role">
          <CardContent className="p-5 space-y-3">
            <div className="font-display font-semibold text-erf-dark">Role</div>
            <Select value={form.role_key} onValueChange={(v) => setForm({ ...form, role_key: v })}
                    disabled={!canManage || isSelf}>
              <SelectTrigger data-testid="user-detail-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.key} value={r.key}
                              disabled={r.key === "OWNER_SUPER_ADMIN" && !canDemoteOwner}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSelf && (
              <div className="text-[11px] font-mono text-erf-dark/60">
                You cannot change your own role.
              </div>
            )}
            <Button onClick={saveRole} disabled={busy || !canManage || isSelf || form.role_key === target.role_key}
                    className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                    data-testid="user-detail-save-role">
              Save role
            </Button>
          </CardContent>
        </Card>

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="user-detail-projects">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-erf-dark">Project Assignments</div>
              <StatusChip status="not_implemented" testId="user-detail-projects-state" />
            </div>
            <div className="text-xs font-nav text-erf-dark/70">
              A per-user project assignment list is not implemented yet (scheduled B2b). Architect project access is
              granted from the Architect Portal invite.
            </div>
          </CardContent>
        </Card>

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="user-detail-mfa">
          <CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-erf-dark">MFA</div>
              <StatusChip status="not_implemented" testId="user-detail-mfa-state" />
            </div>
            <div className="text-xs font-nav text-erf-dark/70">
              MFA enrolment (authenticator apps, passkeys, recovery codes) is not implemented yet. It is scheduled
              right after B2b. Password rotation is already enforced.
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 border-erf-borderCream bg-erf-cream" data-testid="user-access-history">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 text-erf-gold"/>
              <div className="font-display font-semibold text-erf-dark">Access History</div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">
                Latest {history.count} rows · from audit ledger
              </span>
            </div>
            <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
              <table className="w-full erf-table">
                <thead className="bg-erf-creamSecondary">
                  <tr>
                    <th className="text-left px-3 py-2 mono">#</th>
                    <th className="text-left px-3 py-2">Timestamp (UTC)</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Resource</th>
                    <th className="text-left px-3 py-2">Resource ID</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-erf-dark/50 font-mono text-xs">No activity recorded.</td></tr>
                  )}
                  {history.rows.map((r) => (
                    <tr key={r.id} className="border-t border-erf-borderCream/60"
                        data-testid={`user-access-history-row-${r.id}`}>
                      <td className="px-3 py-2 mono text-erf-dark/70">{r.hash_chain_index}</td>
                      <td className="px-3 py-2 mono">{(r.ts || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="px-3 py-2 font-nav">{r.action}</td>
                      <td className="px-3 py-2 mono text-erf-dark/70">{r.resource_type || "—"}</td>
                      <td className="px-3 py-2 mono text-erf-dark/50 text-xs">{r.resource_id ? String(r.resource_id).slice(0, 12) + "…" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminAppShell>
  );
}
